import {FC, useCallback, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  fetchAsyncUserDetails,
  getAllUserDetails,
} from '../../UserPages/accounts/Overview/OverviewSlice/UserDetailsSlice'
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import {myReferralCode, SubmitReferralCode} from '../../../../services/cellcast.v2.types'
import {
  useCreateStaffCodeMutation,
  useRemoveStaffCodeMutation,
} from '../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import {Spinner} from 'react-bootstrap'
import DeleteUser from '../../UserPages/sub-client/Modal/DeleteUser'
import DeleteConfirmationPopup from '../../../Components/DeleteConfirmationPopup'

type AffiliateProps = {}

const Affiliate: FC<AffiliateProps> = (props) => {
  const dispatch = useDispatch()

  const userData = useSelector(getAllUserDetails)

  const [submitCode, submitCodeResponse] = useCreateStaffCodeMutation()
  const [deleteCode] = useRemoveStaffCodeMutation()

  const validationSchema = Yup.object({
    code: Yup.string().min(4).required('Code is required'),
  })

  const initialValues = {
    code: '',
  }
  // action
  const handleSubmitCode = useCallback(
    (values: SubmitReferralCode, helpers: FormikHelpers<SubmitReferralCode>) => {
      submitCode(values).then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            toast.error(res?.data?.message)
          } else {
            dispatch(fetchAsyncUserDetails())
            toast.success(res?.data?.message)
            helpers.resetForm()
          }
        } else if ('error' in res) {
          const resError = res.error as any
          if (resError.data?.message) {
            toast.error(resError.data?.message || '')
          } else {
            toast.error(res?.error.toString() || '')
          }
        }
      })
    },
    [dispatch, submitCode]
  )

  const [show, setShow] = useState(false)
  const [selectedCode, setSelectedCode] = useState('')

  const DeleteModal = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={show}
        handleClose={() => {
          setShow(false)
          setSelectedCode('')
        }}
        Message={`Are you sure, you want to delete "${selectedCode}" code?`}
        confirm={() => {
          deleteCode({
            code: selectedCode,
          }).then((res) => {
            if ('data' in res) {
              if (res.data?.error && Object.keys(res.data?.error).length > 0) {
                toast.error(res?.data?.message)
              } else {
                dispatch(fetchAsyncUserDetails())
                toast.success(res?.data?.message)
              }
            } else if ('error' in res) {
              const resError = res.error as any
              if (resError.data?.message) {
                toast.error(resError.data?.message || '')
              } else {
                toast.error(res?.error.toString() || '')
              }
            }
          })
        }}
      />
    ),
    [deleteCode, dispatch, selectedCode, show]
  )
  const handleRemoveCode = useCallback(
    (code: string) => {
      deleteCode({
        code: code,
      }).then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            toast.error(res?.data?.message)
          } else {
            dispatch(fetchAsyncUserDetails())
            toast.success(res?.data?.message)
          }
        } else if ('error' in res) {
          const resError = res.error as any
          if (resError.data?.message) {
            toast.error(resError.data?.message || '')
          } else {
            toast.error(res?.error.toString() || '')
          }
        }
      })
    },
    [dispatch, submitCode]
  )

  const getStatusCode = (status: string) => {
    switch (status) {
      case myReferralCode.NotSet:
        return <span className='badge badge-light-info'>Not Set</span>
      case myReferralCode.WaitingApproval:
        return <span className='badge badge-light-warning'>Waiting Approval</span>
      case myReferralCode.Active:
        return <span className='badge badge-light-success'>Active</span>
      case myReferralCode.Inactive:
        return <span className='badge badge-light-danger'>Not Active</span>
      case myReferralCode.Rejected:
        return <span className='badge badge-light-danger'>Rejected</span>
    }
  }

  const handleCopy = useCallback((code: string) => {
    navigator.clipboard.writeText(code)
    toast.success('Copied!')
  }, [])

  return (
    <div className='mt-8'>
      {DeleteModal}
      <div className='card p-8'>
        <div className='card-body border-top p-9'>
          <div className='row'>
            <div
              className='col-lg-12 col-md-12 col-xs-12 mb-5 mt-5'
              style={
                userData?.myReferralCodes &&
                Array.isArray(userData?.myReferralCodes) &&
                userData?.myReferralCodes.length > 0
                  ? {borderBottom: 'black dashed 1px '}
                  : {}
              }
            >
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmitCode}
              >
                {({isSubmitting}) => (
                  <Form>
                    <div className='row mb-5'>
                      <div className='col-lg-12 mb-7'>
                        <h4 className='fw-bolder m-0 fs-3'>Create New Referral Code</h4>
                      </div>
                      {userData?.myReferralCodeStatus === 'rejected' && (
                        <div className='col-lg-12 mb-3'>
                          <span className='badge badge-light-danger'>
                            your code is rejected, please try submit again
                          </span>
                        </div>
                      )}
                      <div className='col-lg-3'>
                        <div className='form-group'>
                          <Field
                            type='text'
                            id='code'
                            name='code'
                            className='form-control form-control-flush ps-10 rounded border-gray-200 border'
                            placeholder='Enter your referral code'
                          />
                          <ErrorMessage name='code' component='div' className='text-danger' />
                        </div>
                      </div>
                      <div className='col-lg-4'>
                        <button className='btn btn-primary' type='submit'>
                          {submitCodeResponse.isLoading ? <Spinner size='sm' /> : 'Create Code'}
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          {userData?.myReferralCodes &&
            Array.isArray(userData?.myReferralCodes) &&
            userData?.myReferralCodes.length > 0 && (
              <div className='row'>
                <div className='col-lg-12 mb-5 mt-5'>
                  <h6 className='fw-bolder m-0 fs-3'>My Referral Code</h6>
                </div>
                {userData?.myReferralCodes.map((el: any) => (
                  <div className='col-lg-6 col-xs-12 col-md-6 mb-5'>
                    <div className='row'>
                      <div
                        className='col-lg-3 d-flex justify-content-center align-items-center'
                        style={{
                          fontSize: 20,
                          backgroundColor: '#f5f8fa',
                          padding: 10,
                          paddingLeft: 20,
                          paddingRight: 20,
                          borderRadius: 5,
                          minHeight: 80,
                        }}
                      >
                        <span className='text-truncate'>{el?.code}</span>
                      </div>
                      <div className='col-lg-8 ps-5 d-flex flex-column'>
                        <div className='row mb-2'>
                          <div className='col-md-5'>
                            <label>Status </label>
                          </div>
                          <div className='col-md-7'>{getStatusCode(el?.status)}</div>
                        </div>

                        <div className='row'>
                          <div className='col-md-10 mt-5'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleCopy(el?.code)}
                            >
                              <i className='bi bi-clipboard'></i> copy code
                            </button>
                            <button
                              className='ms-1 btn btn-sm btn-danger'
                              onClick={() => {
                                setSelectedCode(el?.code)
                                setShow(true)
                              }}
                            >
                              <i className='bi bi-trash'></i> delete code
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

export default Affiliate
